import React, { useState, useEffect, useCallback } from 'react';
import api, { siteDatabaseAPI } from '../../services/api';
import { format } from 'date-fns';

// Full screen image modal component
const ImageModal = ({ isOpen, onClose, imageSrc, alt }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors z-10"
        aria-label="Close"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Image container */}
      <div
        className="max-w-[95vw] max-h-[95vh] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageSrc}
          alt={alt}
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        />
      </div>
    </div>
  );
};

// Component to load attendance photos with authentication
// Handles both local mode (direct blob) and R2 mode (presigned URL)
const AuthenticatedAttendancePhoto = ({ siteId, attendanceId, type = 'processed', alt, className, onClick }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      try {
        // Always fetch as blob - works for both modes
        const response = await api.get(`/site-database/${siteId}/attendance/${attendanceId}/photo`, {
          params: { type },
          responseType: 'blob'
        });

        if (!mounted) return;

        // Check if response is JSON (R2 mode returns JSON with downloadUrl)
        const contentType = response.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          // R2 mode: parse JSON to get downloadUrl
          const text = await response.data.text();
          const jsonData = JSON.parse(text);
          if (jsonData.downloadUrl) {
            setImageSrc(jsonData.downloadUrl);
          }
          setLoading(false);
        } else {
          // Local mode: response is the actual file blob
          objectUrl = URL.createObjectURL(response.data);
          setImageSrc(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading attendance photo:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      // Only revoke if it's an object URL (starts with blob:)
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [siteId, attendanceId, type]);

  if (loading) {
    return (
      <div className={`${className} flex flex-col items-center justify-center bg-gray-200 animate-pulse rounded-lg min-h-[200px]`}>
        {/* Photo placeholder skeleton */}
        <svg className="w-12 h-12 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zm4 8a2 2 0 100-4 2 2 0 000 4zm8 4H6l2-4 1.5 2 3-4 3.5 6z" />
        </svg>
        <span className="mt-2 text-xs text-gray-400">Loading photo...</span>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 text-gray-400`}>
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
          <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2" />
          <polyline points="21 15 16 10 5 21" strokeWidth="2" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={`${className} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
      onClick={() => onClick && onClick(imageSrc)}
    />
  );
};

const AttendanceRecords = ({ siteId, refreshTrigger }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [modalImage, setModalImage] = useState({ isOpen: false, src: null, alt: '' });

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const response = await siteDatabaseAPI.getAttendance(siteId, { limit: 50 });
      setRecords(response.data.data || []);
    } catch (err) {
      console.error('Error fetching attendance records:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (siteId) {
      fetchRecords();
    }
  }, [fetchRecords, refreshTrigger, siteId]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatCoordinate = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toFixed(6);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Attendance Records</h2>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Attendance Records</h2>
        <span className="text-sm text-gray-500">{records.length} records</span>
      </div>

      {records.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500">No attendance records yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map((record) => (
            <div
              key={record.id}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Record header - clickable */}
              <button
                onClick={() => toggleExpand(record.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    record.type === 'check_in' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">
                      {record.user?.nickname || record.user?.fullName || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(record.recordedAt), 'MMM dd, yyyy hh:mm a')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    record.type === 'check_in'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {record.type === 'check_in' ? 'Check In' : 'Check Out'}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedId === record.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Expanded details */}
              {expandedId === record.id && (
                <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Photo - clickable to view full size */}
                    <div className="relative">
                      <AuthenticatedAttendancePhoto
                        siteId={siteId}
                        attendanceId={record.id}
                        type="processed"
                        alt={`${record.type === 'check_in' ? 'Check-in' : 'Check-out'} photo`}
                        className="w-full rounded-lg shadow-sm"
                        onClick={(src) => setModalImage({
                          isOpen: true,
                          src,
                          alt: `${record.type === 'check_in' ? 'Check-in' : 'Check-out'} photo - ${record.user?.nickname || record.user?.fullName || 'User'}`
                        })}
                      />
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                        Tap to view
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Location</span>
                        <p className="text-sm text-gray-900 font-mono mt-1">
                          {formatCoordinate(record.latitude)}, {formatCoordinate(record.longitude)}
                        </p>
                        {record.accuracy && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Accuracy: ~{Math.round(record.accuracy)}m
                          </p>
                        )}
                      </div>

                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Time</span>
                        <p className="text-sm text-gray-900 mt-1">
                          {format(new Date(record.recordedAt), 'EEEE, MMMM dd, yyyy')}
                        </p>
                        <p className="text-sm text-gray-600">
                          {format(new Date(record.recordedAt), 'hh:mm:ss a')}
                        </p>
                      </div>

                      {record.notes && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</span>
                          <p className="text-sm text-gray-900 mt-1">{record.notes}</p>
                        </div>
                      )}

                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">User</span>
                        <p className="text-sm text-gray-900 mt-1">
                          {record.user?.nickname || record.user?.fullName || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {record.user?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Google Maps link */}
                  <div className="mt-4">
                    <a
                      href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      View on Google Maps
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Full screen image modal */}
      <ImageModal
        isOpen={modalImage.isOpen}
        onClose={() => setModalImage({ isOpen: false, src: null, alt: '' })}
        imageSrc={modalImage.src}
        alt={modalImage.alt}
      />
    </div>
  );
};

export default AttendanceRecords;
